import classNames from 'classnames';
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import { useRef, useState } from 'react';
import type { To } from 'react-router';
import styled, { keyframes } from 'styled-components';

import { Link } from '@sorare/routing';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LinkBox, LinkOverlay } from '#src/atoms/navigation/Box/index.tsx';
import { proxyUrl } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import { VIDEO_FORMAT, Video } from '#src/atoms/ui/Video/index.tsx';
import { useIsMobile } from '#src/hooks/device/useIsMobile.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const scaleInOut = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
`;

const translate = keyframes`
  0% {
    transform: translateX(0%);
  }
  1% {
    opacity: var(--hover-opacity, 1);
  }
  25% {
    transform: translateX(10%)
  }
  75% {
    transform: translateX(-10%)
  }
  99% {
    opacity: var(--hover-opacity, 1);
  }
  100% {
    transform: translateX(0%);
  }
`;
const Root = styled(LinkBox)`
  --transition-duration: 300ms;
  --border-color: var(--c-nd-100);
  --border-radius: var(--double-unit);
  --border-size: 1.5px;
  --easing: cubic-bezier(0.47, 1.64, 0.41, 0.8);

  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: block;

  min-width: 228px;
  width: 100%;
  border: var(--border-size) solid transparent;
  background:
    linear-gradient(to bottom, var(--c-black), var(--c-black)) padding-box,
    radial-gradient(
        farthest-corner circle at var(--mx) var(--my),
        rgba(255, 255, 255, 0.75) 20%,
        var(--border-color) 40%,
        rgba(0, 0, 0, 0) 100%
      )
      border-box,
    linear-gradient(to bottom, var(--border-color), var(--border-color))
      border-box;
  border-radius: var(--border-radius);

  &:before {
    content: '';
    position: absolute;
    inset: -20%;
    background: var(--bg-picture-url) center / cover no-repeat;
    border-radius: calc(var(--border-radius) - var(--border-size));
    opacity: var(--opacity, 0.3);
    z-index: -1;
    transition: opacity var(--transition-duration) var(--easing);
  }
  &:not(.showBackground) {
    &:before {
      @media ${tabletAndAbove} {
        opacity: 0;
      }
    }
  }

  &:hover:not(.disabled) {
    /* override border color passed as inline style */
    --border-color: var(--primary-color) !important;
    box-shadow: 0 0 var(--double-unit) var(--primary-color);

    &::before {
      opacity: var(--hover-opacity, 1);
    }

    @media ${tabletAndAbove} {
      &::before {
        opacity: var(--hover-opacity, 1);
        animation: 40s ${translate} linear infinite;
      }
      animation: 200ms ${scaleInOut} ease-in-out;
    }
  }
  transition: box-shadow var(--transition-duration) var(--easing);

  @media ${tabletAndAbove} {
    min-width: 284px;
  }
`;

const TopRightLabel = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  position: absolute;
  top: 0;
  right: 0;
  padding: var(--unit);
  font: var(--t-bold) var(--t-label-m);
  border-radius: 0 var(--unit) 0 var(--unit);
  border-color: var(--c-yellow-400);
  color: var(--c-white);
  border-style: solid;
  border-width: 0 0 1px 1px;
  background: var(--c-surface-caution);
  user-select: none;
`;

const BackgroundVideo = styled(Video)`
  position: absolute;
  z-index: -1;
  inset: 0;
  opacity: 0;

  :hover > & {
    opacity: 1;
  }
  transition: opacity var(--transition-duration) var(--easing);
`;

const DEFAULT_MOUSE_POSITION = { x: -100, y: -100 };

type Props = {
  primaryColor?: string;
  borderColor?: string;
  backgroundPictureUrl?: string | null;
  backgroundVideoUrl?: string | null;
  backgroundDarker?: boolean;
  forceShowBackground?: boolean;
  topRightLabel?: ReactNode;
  to?: To;
  onClick?: () => void;
  className?: string;
};
export const AnimatedBackgroundBox = ({
  to,
  primaryColor,
  borderColor = 'var(--c-nd-100)',
  backgroundVideoUrl,
  backgroundPictureUrl,
  backgroundDarker,
  children,
  forceShowBackground = false,
  topRightLabel = null,
  onClick,
  className,
}: PropsWithChildren<Props>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mousePosition, setMousePosition] = useState(DEFAULT_MOUSE_POSITION);
  const isMobile = useIsMobile();

  const clickable = to || onClick;

  return (
    <Root
      ref={rootRef}
      onMouseMove={({ clientX: mx, clientY: my }) => {
        if (isMobile || !clickable) {
          return;
        }

        const rootRect = rootRef?.current?.getBoundingClientRect();
        if (rootRect) {
          setMousePosition({
            x: ((mx - rootRect.left) * 100) / rootRect.width,
            y: ((my - rootRect.top) * 100) / rootRect.height,
          });
        }
      }}
      onMouseLeave={() => {
        setMousePosition(DEFAULT_MOUSE_POSITION);
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }}
      onMouseEnter={() => {
        videoRef.current?.play();
      }}
      style={
        {
          '--primary-color': primaryColor,
          '--border-color': borderColor,
          '--bg-video-url': `url(${backgroundVideoUrl})`,
          '--bg-picture-url': backgroundPictureUrl
            ? `url(${proxyUrl(backgroundPictureUrl, { cropWidth: 320 })})`
            : undefined,
          '--mx': `${mousePosition.x}%`,
          '--my': `${mousePosition.y}%`,
          '--opacity': !clickable || backgroundDarker ? 0.1 : undefined,
        } as CSSProperties
      }
      className={classNames(className, {
        showBackground: forceShowBackground,
        disabled: !clickable,
      })}
    >
      {!!backgroundVideoUrl && (
        <BackgroundVideo
          ref={videoRef}
          sources={[{ src: backgroundVideoUrl, type: VIDEO_FORMAT.MP4 }]}
          muted
          hideMutedIcon
          playsInline
          onEnded={() => {
            if (videoRef.current) {
              videoRef.current.pause();
              videoRef.current.currentTime = 0;
              videoRef.current.play();
            }
          }}
        />
      )}

      {children}
      {!!clickable && (
        <LinkOverlay
          {...(to && { to, as: Link })}
          {...(!to && { as: ButtonBase })}
          onClick={onClick}
        />
      )}
      {!!topRightLabel && <TopRightLabel>{topRightLabel}</TopRightLabel>}
    </Root>
  );
};
